import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import Button from "../components/common/Button";
import FormInput from "../components/common/FormInput";
import Toast from "../components/common/Toast";
import OtpModal from "../components/common/OtpModal";
import {
  useLoginUserMutation,
  useVerifyEmailAddressMutation,
} from "../services/auth/authApi";
import { resetApiCache } from "../services/api/resetApiCache";
import {
  clearSelectedSignupRole,
  clearSignupVerified,
} from "../store/signupStorage";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import "@fontsource/work-sans/400.css";
import { AuthNavBar } from "../components/Navbar";

const NEW_OTP_SENT_MESSAGE = "A new otp is sent to your email.";
const EMAIL_VERIFICATION_REQUIRED_MESSAGE =
  "Please verify your email before logging in.";

const OTP_MODAL_MESSAGES = [
  NEW_OTP_SENT_MESSAGE,
  EMAIL_VERIFICATION_REQUIRED_MESSAGE,
];

const getLoginErrorMessage = (errorOrResponse) => {
  if (
    errorOrResponse?.message &&
    typeof errorOrResponse.message === "string"
  ) {
    return errorOrResponse.message;
  }

  const data = errorOrResponse?.data;
  if (typeof data === "string") return data;
  if (data && typeof data.message === "string") return data.message;
  if (typeof errorOrResponse?.error === "string") return errorOrResponse.error;

  return "Login failed. Please try again.";
};

const getResponseBody = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  if (
    payload?.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data) &&
    "success" in payload.data
  ) {
    return payload.data;
  }

  return payload;
};

const shouldOpenOtpModal = (payload) => {
  const body = getResponseBody(payload);
  if (!body || body.success !== false || typeof body.message !== "string") {
    return false;
  }

  const normalizedMessage = body.message.trim().toLowerCase();
  return OTP_MODAL_MESSAGES.some(
    (message) => normalizedMessage === message.toLowerCase(),
  );
};

const isValidLoggedInUser = (user) => {
  if (!user || typeof user !== "object") return false;
  return Boolean(user._id || user.id || user.email);
};

const Login = () => {
  const [loginUser, { isLoading: isLoggingIn }] = useLoginUserMutation();
  const [verifyEmailAddress, { isLoading: isVerifyingOtp }] =
    useVerifyEmailAddressMutation();
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "error",
  });
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpInfoMessage, setOtpInfoMessage] = useState("");
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const otpInputsRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    clearSelectedSignupRole();
    clearSignupVerified();
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
  });

  const showErrorToast = (message) => {
    setToast({
      show: true,
      title: "Login failed",
      message,
      variant: "error",
    });
  };

  const showSuccessToast = (message) => {
    setToast({
      show: true,
      title: "Success",
      message,
      variant: "success",
    });
  };

  const openOtpModal = (credentials, infoMessage = NEW_OTP_SENT_MESSAGE) => {
    setPendingCredentials(credentials);
    setOtpValues(["", "", "", ""]);
    setOtpError("");
    setOtpInfoMessage(infoMessage);
    setShowOtpModal(true);
  };

  const completeLogin = (loggedInUser) => {
    resetApiCache();
    localStorage.setItem("loggedInUser", JSON.stringify(loggedInUser));

    if (loggedInUser?.role === "student") {
      navigate("/student");
      return;
    }
    if (loggedInUser?.role === "instructor") {
      navigate("/instructor/dashboard");
      return;
    }

    showErrorToast(
      "Unable to determine your account type. Please contact support.",
    );
  };

  const onSubmit = async (data) => {
    try {
      const response = await loginUser(data).unwrap();

      if (shouldOpenOtpModal(response)) {
        openOtpModal(data, response?.message || NEW_OTP_SENT_MESSAGE);
        return;
      }

      if (response?.success === false) {
        showErrorToast(getLoginErrorMessage(response));
        return;
      }

      const loggedInUser = response?.data?.[0];
      if (!isValidLoggedInUser(loggedInUser)) {
        showErrorToast(
          typeof response?.message === "string" && response.message
            ? response.message
            : "No user found. Please check your email and password.",
        );
        return;
      }

      completeLogin(loggedInUser);
    } catch (error) {
      if (shouldOpenOtpModal(error) || shouldOpenOtpModal(error?.data)) {
        openOtpModal(
          data,
          error?.data?.message || error?.message || NEW_OTP_SENT_MESSAGE,
        );
        return;
      }

      showErrorToast(getLoginErrorMessage(error));
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextValues = [...otpValues];
    nextValues[index] = digit;
    setOtpValues(nextValues);
    setOtpError("");

    if (digit && index < 3) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otpValues.join("");
    if (!/^\d{4}$/.test(otpCode)) {
      setOtpError("Please enter a valid 4-digit OTP");
      return;
    }

    const email = pendingCredentials?.email || getValues("email");
    if (!email) {
      setOtpError("Email is missing. Please try logging in again.");
      return;
    }

    try {
      setOtpError("");
      setOtpInfoMessage("");

      const response = await verifyEmailAddress({
        email,
        otp: otpCode,
      }).unwrap();

      if (response?.success === false) {
        setOtpError(
          response?.message || "OTP verification failed. Please try again.",
        );
        return;
      }

      setShowOtpModal(false);
      showSuccessToast(
        response?.message || "Email verified successfully. Please log in again.",
      );

      if (pendingCredentials?.email && pendingCredentials?.password) {
        try {
          const loginResponse = await loginUser(pendingCredentials).unwrap();

          if (shouldOpenOtpModal(loginResponse)) {
            openOtpModal(
              pendingCredentials,
              loginResponse?.message || NEW_OTP_SENT_MESSAGE,
            );
            return;
          }

          if (loginResponse?.success === false) {
            showErrorToast(getLoginErrorMessage(loginResponse));
            return;
          }

          const loggedInUser = loginResponse?.data?.[0];
          if (isValidLoggedInUser(loggedInUser)) {
            completeLogin(loggedInUser);
            return;
          }
        } catch (loginError) {
          if (
            shouldOpenOtpModal(loginError) ||
            shouldOpenOtpModal(loginError?.data)
          ) {
            openOtpModal(
              pendingCredentials,
              loginError?.data?.message ||
                loginError?.message ||
                NEW_OTP_SENT_MESSAGE,
            );
            return;
          }
          showErrorToast(getLoginErrorMessage(loginError));
        }
      }
    } catch (error) {
      setOtpError(
        error?.data?.message || error?.message || "OTP verification failed",
      );
    }
  };

  const handleResendOtp = async () => {
    const credentials =
      pendingCredentials ||
      ({
        email: getValues("email"),
        password: getValues("password"),
      });

    if (!credentials?.email || !credentials?.password) {
      setOtpError("Please enter your email and password again to resend OTP.");
      return;
    }

    try {
      setOtpError("");
      const response = await loginUser(credentials).unwrap();

      if (shouldOpenOtpModal(response) || response?.success !== false) {
        setOtpValues(["", "", "", ""]);
        setOtpInfoMessage(response?.message || "A new OTP has been sent.");
        otpInputsRef.current[0]?.focus();
        return;
      }

      setOtpError(response?.message || "Failed to resend OTP.");
    } catch (error) {
      if (shouldOpenOtpModal(error) || shouldOpenOtpModal(error?.data)) {
        setOtpValues(["", "", "", ""]);
        setOtpInfoMessage(
          error?.data?.message || error?.message || "A new OTP has been sent.",
        );
        otpInputsRef.current[0]?.focus();
        return;
      }

      setOtpError(
        error?.data?.message || "Failed to resend OTP. Please try again.",
      );
    }
  };

  return (
    <div>
      <AuthNavBar />
      <div className="min-h-screen bg-[#EFEFFD] flex items-center px-6 py-16 lg:py-24 xl:py-36">
        <div className="mx-auto w-full max-w-[420px] font-['Work_Sans'] rounded-[17px] bg-[#F5F5F5] px-5 sm:px-[50px] py-10 sm:pt-[62px] sm:pb-[52px] shadow-[0_2px_5.7px_#00000026]">
          <h1 className="text-center  tracking-[-2%] text-xl sm:text-2xl font-semibold !leading-7 text-black">
            Log in to Get Started
          </h1>

          <form
            className="mt-5 sm:mt-8"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormInput
              id="email"
              label="Email*"
              type="email"
              placeholder="Registered email"
              labelClassName="block font-['Work_Sans'] text-base sm:text-lg !leading-[21px] tracking-[-2%]"
              inputClassName="mt-1.5 h-10 sm:h-12 text-sm sm:text-base rounded-lg  border-[0.5px] border-[#B9B3C2] px-2 sm:px-4  font-medium text-[#8C8CA1] placeholder:text-[#8C8CA1] focus:border-[#923D8C]"
              errorMessage={errors.email?.message}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address",
                },
              })}
              autoComplete="off"
            />

            <FormInput
              className="mt-3 sm:mt-5"
              id="password"
              label="Password*"
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              labelClassName="block font-['Work_Sans'] text-base sm:text-lg !leading-[21px] tracking-[-2%] text-black"
              inputClassName="mt-1.5 h-10 sm:h-12 text-sm sm:text-base rounded-lg border-[0.5px] border-[#B9B3C2] px-2 sm:px-4 pr-10 sm:pr-12 font-medium text-[#8C8CA1] placeholder:text-[#8C8CA1] focus:border-[#923D8C]"
              errorMessage={errors.password?.message}
              rightContent={
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="pointer-events-auto text-[#8C8CA1] hover:text-[#923D8C]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
              {...register("password", {
                required: "Password is required",
              })}
              autoComplete="new-password"
            />

            <Link
              to="/forgot-password"
              className="mt-3 sm:mt-7  text-sm sm:text-base block text-right !leading-[18px] text-[#000066]"
            >
              Forget Password?
            </Link>

            <Button
              type="submit"
              disabled={isSubmitting || isLoggingIn}
              btnText={isSubmitting || isLoggingIn ? "Logging in..." : "Login"}
              btnClassName="mt-7 sm:mt-10 tracking-[4%] font-['Work_Sans']  w-full rounded-full bg-[#923D8C]  font-semibold !leading-[19px] text-white shadow-[0_6px_2px_-4_#0E0E2C1A] tracking-[4%] py-1.5"
            />

            <p className=" mt-8 text-center text-black text-[15px] leading-[18px]">
              Create an account?{" "}
              <Link to="/signup" className="text-[#923D8C]">
                Sign up
              </Link>{" "}
            </p>
          </form>
        </div>
      </div>

      <OtpModal
        isOpen={showOtpModal}
        otpValues={otpValues}
        otpError={otpError}
        otpInfoMessage={otpInfoMessage}
        isLoading={isVerifyingOtp || isLoggingIn}
        title="Enter OTP"
        subtitle="We have sent a OTP to your Email"
        onChange={handleOtpChange}
        onKeyDown={handleOtpKeyDown}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        inputRefs={otpInputsRef}
      />

      {toast.show ? (
        <Toast
          title={toast.title}
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast((current) => ({ ...current, show: false }))}
        />
      ) : null}
    </div>
  );
};

export default Login;
